import { Box, Container, Typography, Grid, Card, CardActionArea, CardMedia, Stack, Chip, Alert } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import { lightTheme, brand } from '../components/themes/main_theme.js';
import Header from '../components/menu/Header.tsx';
import { useBlogPosts, BlogPostItem } from '../data/cms.ts';

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';

/**
 * Blog index.
 *
 * Posts are written in the CMS under "Blog Posts" and appear here as soon as
 * their publish date arrives.
 */
export const BlogList = () => {
  const navigate = useNavigate();
  const { items, loading } = useBlogPosts();

  return (
    <ThemeProvider theme={lightTheme}>
      <Header name={'BLOG'} />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 3 }, mb: 10 }}>
        <Typography variant="h2" color="primary" sx={{ mb: 1 }}>
          From the field
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 5, maxWidth: 680 }}>
          Stories, updates and reflections from the work in Guatemala.
        </Typography>

        {!loading && items.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            No posts yet. New posts written in the admin will appear here.
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {items.map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id} sx={{ display: 'flex' }}>
              <CardActionArea
                onClick={() => navigate(`/blog/${post.id}`)}
                sx={{
                  height: '100%',
                  borderRadius: 5,
                  transition: 'transform .2s, box-shadow .2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}
              >
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5 }}>
                  {post.coverUrl && (
                    <CardMedia
                      component="img"
                      image={post.coverUrl}
                      sx={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', borderRadius: 3 }}
                    />
                  )}
                  <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="overline" color="secondary">
                      {formatDate(post.publishedAt)}
                    </Typography>
                    <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                      {post.title}
                    </Typography>
                    <Typography color="text.secondary" variant="body2" sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {post.body}
                    </Typography>
                    {post.author && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto', pt: 1.5 }}>
                        {post.author}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </CardActionArea>
            </Grid>
          ))}
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

/** A single post. */
export const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { items, loading } = useBlogPosts();
  const post: BlogPostItem | undefined = items.find((p) => p.id === id);

  return (
    <ThemeProvider theme={lightTheme}>
      <Header name={'BLOG'} />
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 3 }, mb: 10 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')} sx={{ mb: 3, px: 0, color: 'secondary.main' }}>
          All posts
        </Button>

        {loading && <Typography color="text.secondary">Loading…</Typography>}

        {!loading && !post && (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            That post could not be found — it may have been unpublished.
          </Alert>
        )}

        {post && (
          <>
            <Typography variant="overline" color="secondary">{formatDate(post.publishedAt)}</Typography>
            <Typography variant="h1" color="primary" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
              {post.title}
            </Typography>
            {post.author && (
              <Typography color="text.secondary" sx={{ mb: 3 }}>By {post.author}</Typography>
            )}
            {post.coverUrl && (
              <Box
                component="img"
                src={post.coverUrl}
                alt=""
                sx={{ width: '100%', borderRadius: 4, mb: 4, display: 'block' }}
              />
            )}
            {post.tags?.length ? (
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {post.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(165,92,8,0.1)', color: 'secondary.main' }} />
                ))}
              </Stack>
            ) : null}
            {/* Body is stored as plain text; preserve the author's line breaks
                rather than rendering raw HTML from the CMS. */}
            <Typography
              component="div"
              sx={{ whiteSpace: 'pre-wrap', fontSize: '1.0625rem', lineHeight: 1.75, color: 'text.primary' }}
            >
              {post.body}
            </Typography>
            <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${brand.hairline}` }}>
              <Button variant="outlined" onClick={() => navigate('/blog')}>Read more posts</Button>
            </Box>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default BlogList;
